import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  House, CreditCard, Users, Receipt, Key, ShieldCheck, Broadcast, Wallet,
  ListChecks, SignOut, FileArrowUp, ChartBar, BookOpen,
} from "@phosphor-icons/react";
import { mAuth } from "@/lib/api";
import { StatusPill } from "@/components/Layout";
import { BlonixWordmark } from "@/components/BlonixLogo";

const NAV = [
  { to: "/m", label: "Dashboard", icon: House, end: true },
  { to: "/m/wallet", label: "Wallet", icon: Wallet },
  { to: "/m/cards", label: "Cards", icon: CreditCard },
  { to: "/m/users", label: "Cardholders", icon: Users },
  { to: "/m/transactions", label: "Transactions", icon: Receipt },
];
const NAV2 = [
  { to: "/m/api-keys", label: "API Keys", icon: Key },
  { to: "/m/security", label: "Security", icon: ShieldCheck },
  { to: "/m/webhooks", label: "Webhooks", icon: Broadcast },
  { to: "/m/webhook-events", label: "Webhook Log", icon: ListChecks },
  { to: "/m/usage", label: "API Usage", icon: ChartBar },
  { to: "/m/audit", label: "Audit Logs", icon: ListChecks },
  { to: "/m/kyb", label: "KYB Documents", icon: FileArrowUp },
  { to: "/m/api-docs", label: "API Reference", icon: BookOpen },
];

export default function MerchantLayout({ children }) {
  const navigate = useNavigate();
  const user = mAuth.user();
  const merchant = user?.merchant;

  const onLogout = async () => { await mAuth.logout(); navigate("/m/login"); };

  return (
    <div className="min-h-screen flex bg-zinc-100" data-testid="merchant-shell">
      <aside className="w-64 shrink-0 bg-zinc-950 text-zinc-300 flex flex-col fixed inset-y-0 left-0 z-30" data-testid="m-sidebar">
        <div className="px-6 py-6 border-b border-zinc-900">
          <BlonixWordmark className="h-5 w-auto" />
          <div className="min-w-0 mt-3">
            <div className="font-display text-base font-semibold text-white tracking-tight truncate">{merchant?.company_name || "Merchant"}</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Merchant Portal</div>
          </div>
        </div>

        {merchant?.kyb_status !== "approved" && (
          <div className="mx-3 mt-3 mb-1 p-3 rounded-sm bg-amber-950/50 border border-amber-900 text-amber-200 text-[11px]">
            <div className="font-semibold mb-1">KYB {merchant?.kyb_status || "pending"}</div>
            Upload required documents to activate live API access.
          </div>
        )}

        <nav className="flex-1 overflow-y-auto sb-scroll px-3 py-4 space-y-6">
          <div>
            <div className="px-3 mb-2 text-[10px] uppercase tracking-[0.18em] text-zinc-600">Operate</div>
            <ul className="space-y-0.5">{NAV.map((it) => <SideLink key={it.to} {...it} />)}</ul>
          </div>
          <div>
            <div className="px-3 mb-2 text-[10px] uppercase tracking-[0.18em] text-zinc-600">Configure</div>
            <ul className="space-y-0.5">{NAV2.map((it) => <SideLink key={it.to} {...it} />)}</ul>
          </div>
        </nav>

        <div className="border-t border-zinc-900 p-3">
          <div className="px-3 py-2 flex items-center gap-3">
            <div className="h-8 w-8 rounded-sm bg-zinc-800 flex items-center justify-center text-xs font-semibold text-white">
              {(user?.name || "M").slice(0, 1).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-xs font-medium text-white truncate" data-testid="m-sidebar-user">{user?.name}</div>
              <div className="text-[10px] text-zinc-500 truncate">{user?.email}</div>
            </div>
            <button onClick={onLogout} data-testid="m-logout-btn" className="text-zinc-400 hover:text-white transition-colors duration-150" title="Sign out">
              <SignOut size={16} />
            </button>
          </div>
        </div>
      </aside>

      <main className="flex-1 min-w-0 ml-64">
        <div className="page-enter">{children}</div>
      </main>
    </div>
  );
}

function SideLink({ to, label, icon: Icon, end }) {
  return (
    <li>
      <NavLink
        to={to}
        end={end}
        data-testid={`m-nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
        className={({ isActive }) =>
          [
            "group flex items-center gap-3 px-3 py-2 text-sm rounded-sm transition-colors duration-150",
            isActive
              ? "bg-zinc-900 text-white border-l-2 border-blue-500 pl-[10px]"
              : "text-zinc-400 hover:text-white hover:bg-zinc-900",
          ].join(" ")
        }
      >
        <Icon size={16} weight="regular" />
        <span>{label}</span>
      </NavLink>
    </li>
  );
}

export { StatusPill };
